import * as PIXI from 'pixi.js';

import { AlphaAttribute, ColorAttribute } from '../attributes';
import { Config } from '../../video-data/config';
import { DynamicStylingMixin } from '../dynamicStylingMixin';
import type { Resolution, DestroyOptions } from '../../types';
import type { BaseGroup } from '../BaseGroup';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';

/**
 * A ColorAsset Object will load the specified solid color.
 */
export class ColorAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = 'ColorAsset';

    #source: PIXI.Sprite | null = null;
    #mediaTargetResolution: Resolution;

    @ColorAttribute()
    accessor sourceColor = 0; // black
    @AlphaAttribute()
    accessor sourceAlpha = 1.0; // fully opaque

    constructor(container: BaseGroup, settings: BaseAssetSettings) {
        super(container, settings);
        this.#mediaTargetResolution = this.getReferenceResolution();
    }

    /**
     * The resolution that best fits the media into the current slide.  This is usually either root resolution, or
     * the resolution of the resulting visible area when media is masked.
     */
    get mediaTargetResolution(): Resolution {
        return this.#mediaTargetResolution;
    }

    set mediaTargetResolution(resolution: Resolution) {
        this.#mediaTargetResolution.width = resolution.width;
        this.#mediaTargetResolution.height = resolution.height;

        if (this.#source === null) {
            return;
        }

        this.applyMediaTargetResolution();
    }

    applyMediaTargetResolution() {
        if (!this.#mediaTargetResolution) {
            return;
        }
        this._pixiContainer.width = this.#mediaTargetResolution.width;
        this._pixiContainer.height = this.#mediaTargetResolution.height;
    }

    override _build() {
        this.clearAnimation();

        if (this.#source) {
            this.#source.destroy();
            this.#source = null;
        }
        this.#source = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._addPixiChild(this.#source);
        super._build();

        // set target resolution
        this.applyMediaTargetResolution();

        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this._applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this._applyAnimation();
        }

        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        if (!this.#source) {
            return;
        }
        this.#source.tint = this.sourceColor;
        this.#source.alpha = this.sourceAlpha;
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.#source = null;
    }

    /**
     * This asset doesn't have to load anything remotely so it should "always load"
     */
    override get _shouldLoad() {
        return true;
    }

    override applyTransformation() {
        this._pixiContainer.alpha = this.opacity;
    }

    // createdFrom is intentionally unserialized: the only STICKER-created ColorAsset is a transient match-cut duplicate excluded from saves.
    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['mask', 'sourceColor', 'sourceAlpha']);
    }

    override get editableProperties(): string[] {
        return super.editableProperties.concat(['sourceColor', 'sourceAlpha']);
    }

    // A ColorAsset morph-pairs ONLY as part of a match-cut: one side is the transient duplicate the freeze
    // creates, paired with its same-color original. The isMatchCutDuplicate gate stops ordinary morphs
    // (scene sequences, swap-scene) from pairing two same-color backgrounds.
    override isMorphMatch(other: BaseAsset): boolean {
        const otherColor = other as ColorAsset;
        if (!Config.isColorAsset(otherColor)) return false;
        if (!this.isMatchCutDuplicate && !otherColor.isMatchCutDuplicate) return false;
        return this.sourceColor === otherColor.sourceColor;
    }
}
